

import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import ServiceProviderChip from '@/components/shared/chips/ServiceProviderChip.vue';
import {EServiceProviderType} from '@/model/Service/serviceProviderInterfaces';
import {IChipStyle} from '@/model/chipStyleInterfaces';

// Vinted has no dedicated EServiceProviderType, so it's matched by name instead of type.
const VINTED_PATH = 'M19.316 0c-.258 0-.571.217-1.415.953-.3.108-.627.027-1.008.613-2.15 3.09-3.825 14.648-5.255 17.984-.286-1.444-.885-10.837-1.116-13.41-.028-.477.027-1.076.027-1.43 0-2.368-.516-3.567-2.886-3.567-1.198 0-2.382.436-3.008 1.226-.299.408-.409.708-.409 1.443 0 4.915 1.171 12.973 2.478 18.228C7.132 23.688 8.603 24 9.99 24c.654 0 1.307-.081 2.233-.544 3.212-1.567 4.07-5.84 4.9-9.993.15-.749.899-4.37 1.253-6.275.476-2.6 1.02-5.54 1.347-6.617C19.833.245 19.63 0 19.317 0z';
const VINTED_COLOR = '#007782';

// Source: https://upload.wikimedia.org/wikipedia/commons/4/4a/Amazon_icon.svg
const AMAZON_VIEWBOX = '2.167 .438 251.038 259.969';
const AMAZON_PATHS = [
  {
    fill: '#f90',
    d: 'm221.503 210.324c-105.235 50.083-170.545 8.18-212.352-17.271-2.587-1.604-6.984.375-3.169 4.757 13.928 16.888 59.573 57.593 119.153 57.593 59.621 0 95.09-32.532 99.527-38.207 4.407-5.627 1.294-8.731-3.16-6.872zm29.555-16.322c-2.826-3.68-17.184-4.366-26.22-3.256-9.05 1.078-22.634 6.609-21.453 9.93.606 1.244 1.843.686 8.06.127 6.234-.622 23.698-2.826 27.337 1.931 3.656 4.79-5.57 27.608-7.255 31.288-1.628 3.68.622 4.629 3.68 2.178 3.016-2.45 8.476-8.795 12.14-17.774 3.639-9.028 5.858-21.622 3.71-24.424z',
  },
  {
    fill: '#000',
    d: 'm150.744 108.13c0 13.141.332 24.1-6.31 35.77-5.361 9.489-13.853 15.324-23.341 15.324-12.952 0-20.495-9.868-20.495-24.432 0-28.75 25.76-33.968 50.146-33.968zm34.015 82.216c-2.23 1.992-5.456 2.135-7.97.806-11.196-9.298-13.189-13.615-19.356-22.487-18.502 18.882-31.596 24.527-55.601 24.527-28.37 0-50.478-17.506-50.478-52.565 0-27.373 14.85-46.018 35.96-55.126 18.313-8.066 43.884-9.489 63.43-11.718v-4.365c0-8.018.616-17.506-4.08-24.432-4.128-6.215-12.003-8.777-18.93-8.777-12.856 0-24.337 6.594-27.136 20.257-.57 3.037-2.799 6.026-5.835 6.168l-32.735-3.51c-2.751-.618-5.787-2.847-5.028-7.07 7.543-39.66 43.36-51.616 75.43-51.616 16.415 0 37.858 4.365 50.81 16.795 16.415 15.323 14.849 35.77 14.849 58.02v52.565c0 15.798 6.547 22.724 12.714 31.264 2.182 3.036 2.657 6.69-.095 8.966-6.879 5.74-19.119 16.415-25.855 22.393l-.095-.095',
  },
];

@Component({})
export default class ServiceProviderBrandIcon extends Vue {

  @Prop({default: null})
  protected type!: string | null;

  @Prop({default: ''})
  protected name!: string;

  // When set, these override the generic service-provider-type icon/color/tooltip (used e.g. for the
  // collect/delivery/done icon in Route.vue) but are still superseded by an Amazon/Vinted brand match.
  @Prop({default: null})
  protected fallbackIcon!: string | null;

  @Prop({default: null})
  protected fallbackColor!: string | null;

  @Prop({default: null})
  protected fallbackTooltip!: string | null;

  @Prop({default: 'x-small'})
  protected size!: 'x-small' | 'small';

  protected vintedPath = VINTED_PATH;
  protected vintedColor = VINTED_COLOR;
  protected amazonViewBox = AMAZON_VIEWBOX;
  protected amazonPaths = AMAZON_PATHS;

  protected get isVinted(): boolean {
    return /vinted/i.test(this.name || '');
  }

  protected get isAmazon(): boolean {
    return this.type === EServiceProviderType.amazon_return || /amazon/i.test(this.name || '');
  }

  protected get typeStyle(): IChipStyle | null {
    return this.type ? ServiceProviderChip.chipStyleOf(this.type as EServiceProviderType) : null;
  }

  protected get typeLabel(): string {
    return this.$t('services.provider.fields.type.values.' + this.type) as string;
  }

  protected get hasCustomFallback(): boolean {
    return this.fallbackIcon !== null;
  }

  protected get resolvedIcon(): string {
    if (this.hasCustomFallback) { return this.fallbackIcon as string; }
    return this.typeStyle ? this.typeStyle.icon : 'mdi-storefront-outline';
  }

  protected get resolvedColor(): string {
    if (this.hasCustomFallback) { return this.fallbackColor || 'grey'; }
    return this.typeStyle ? this.typeStyle.color : 'grey darken-1';
  }

  protected get resolvedTooltip(): string | null {
    if (this.hasCustomFallback) { return this.fallbackTooltip; }
    return this.typeStyle ? this.typeLabel : null;
  }

  protected get sizePx(): number {
    return this.size === 'small' ? 18 : 14;
  }

}

